import React, { useEffect } from 'react';
import { Box, Button, CircularProgress, Divider, Paper, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../api/auth/authSlice';
import PartnersSelectField from './PartnersSelectField';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFieldArray, useForm } from 'react-hook-form';
import { useVendorFeatures } from '../../../hooks/useVendorFeatures';
import { OrganizationValidation } from '../../../validations/OrganizationValidation';
import { map } from 'lodash';
import TopBar from '../../../components/TopBar';
import CustomTextInput from '../../../components/CustomTextInput';
import { FormCheckbox } from '../../../components/FormCheckbox';

const OrganizationForm = ({
  onSubmit,
  onCancel,
  initialData = {},
  apiErrors = {},
  edit = false,
  readOnly = false,
  isLoading = false,
}) => {
  const theme = useTheme();
  let user = useSelector(selectUser);
  const { configurableFeatures, featuresByKey, isLoading: isVendorFeaturesLoading } = useVendorFeatures();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(OrganizationValidation()),
    defaultValues: {
      ...initialData,
      partner: initialData.partner_id,
      features: {},
    },
  });

  useEffect(() => {
    if (isVendorFeaturesLoading || Object.keys(featuresByKey).length === 0) {
      return;
    }

    const featureDefaults = Object.fromEntries(
      Object.entries(featuresByKey).map(([key, policy]) => [key, policy.default]),
    );

    reset({
      ...initialData,
      partner: initialData.partner_id,
      features: {
        ...featureDefaults,
        ...initialData?.features,
      },
    });
  }, [featuresByKey, initialData, isVendorFeaturesLoading, reset]);

  useEffect(() => {
    map(apiErrors, (value, key) => {
      setError(key, { message: value, type: 'string' });
    });
  }, [setError, apiErrors]);

  const handleFormSubmit = async (data) => {
    console.log(data);
    const payload = {
      ...data,
      contact_emails: data.contact_emails?.filter((email) => email.trim() !== '') || null,
      // TODO-PERMISSION: Here if the user is partner_admin then send his Id else send the partner_id
      partner_id: !edit ? (user.role === 'partner' ? user._id : data.partner.id) : data.partner_id,
    };
    onSubmit(payload);
  };

  const {
    fields: contact_emails,
    append: addContactEmail,
    remove: removeContactEmail,
  } = useFieldArray({
    control,
    name: 'contact_emails',
  });

  return (
    <Paper elevation={0} sx={{ p: '1rem' }}>
      {!readOnly && (
        <Box sx={{ pt: 2, pb: 4 }}>
          <Grid
            container
            sx={{
              display: 'block',
            }}
          >
            <Grid xs={12}>
              <TopBar title={'Organization Form'} identifier={initialData?.stripe_customer_id}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(1) }}>
                  <Button
                    data-testid="organization-profile-cancel-button"
                    key={'cancel'}
                    variant="outlined"
                    onClick={onCancel}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="organization-profile-save-button"
                    key={'save'}
                    variant="contained"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isLoading}
                  >
                    Save
                  </Button>
                </Box>
              </TopBar>
            </Grid>
          </Grid>
        </Box>
      )}
      <Box>
        <Grid container spacing={2}>
          <Grid size={6}>
            <CustomTextInput
              data-testid="organization-id-input"
              name="id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={'Organization ID (UUID)'}
              disabled={edit}
            />
          </Grid>
          <Grid size={6}>
            <CustomTextInput
              data-testid="name-input"
              name="name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={'Organization Name'}
            />
          </Grid>
          <Grid size={6}>
            <CustomTextInput
              data-testid="email-input"
              name="email"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={'Email'}
              disabled={edit}
            />
          </Grid>
          {/*TODO-PERMISSION: Check for the roles. Specifically who should select the partner*/}
          {/*{user.role === 'SUBSCRIPTION_ADMIN' && !is_embedded_partner && (*/}
          <Grid size={6}>
            <PartnersSelectField
              data-testid="partner-select"
              label="Partner"
              name="partner"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={edit}
            />
          </Grid>
          {/*)}*/}
        </Grid>

        <Divider textAlign="center" sx={{ mt: '2rem', mb: '2rem' }}>
          <Typography variant="h6" color="text.secondary">
            Contact Emails
          </Typography>
        </Divider>

        <Grid container columnSpacing={1} rowSpacing={2}>
          {contact_emails.map((email, index) => (
            <React.Fragment key={`${email.id}`}>
              <Grid size={{ xs: 9, lg: 4.5, xl: 5 }}>
                <CustomTextInput
                  data-testid="email-input"
                  name={`contact_emails.${index}`}
                  control={control}
                  errors={!!errors.contact_emails}
                  readOnly={readOnly}
                  label={`Email ${index + 1}`}
                  helperText={errors.contact_emails ? errors.contact_emails[index]?.message : ''}
                  placeholder={'Enter Contact Email'}
                />
              </Grid>
              <Grid size={{ xs: 3, lg: 1.5, xl: 1 }}>
                {!readOnly && (
                  <Button
                    color="error"
                    onClick={() => {
                      removeContactEmail(index);
                    }}
                    disabled={contact_emails.length === 1 || readOnly}
                    variant="outlined"
                  >
                    Remove
                  </Button>
                )}
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
        <Box
          sx={{
            mt: 2,
          }}
        >
          {!readOnly && (
            <Button variant="outlined" color="primary" onClick={() => addContactEmail('')}>
              Add Another Email
            </Button>
          )}
        </Box>

        {/* TODO-PERMISSION: Should not show the features below if the partner is embedded */}
        {
          <>
            <Divider textAlign="center" sx={{ mt: '2rem', mb: '2rem' }}>
              <Typography variant="h6" color="text.secondary">
                Features
              </Typography>
            </Divider>

            {isVendorFeaturesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Grid container>
                {configurableFeatures.map(({ key, display_name: displayName }) => (
                  <Grid size={4} key={key}>
                    <FormCheckbox name={`features.${key}`} control={control} label={displayName} readOnly={readOnly} />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        }
      </Box>
    </Paper>
  );
};

export default OrganizationForm;
